/**
 * DevizTab - Generator Deviz Ofertă pentru lucrări de construcții
 * Cu bibliotecă devize și auto-salvare ciornă
 * 
 * Refactored: DevizLibrary component extracted to deviz/DevizLibrary.jsx
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { safeOpenHTMLWindow, sanitizeHTML } from "@/utils/safeHTML";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { FileSpreadsheet, Plus, Trash2, Save, Printer, Eye, FolderOpen, FileText, Edit, ArrowLeft, PlusCircle, Download, Package, ShoppingCart, Loader2, Search, Check, AlertTriangle, Link, Calculator, Target, ChevronDown, AlertCircle, Copy, ChevronLeft, ChevronRight, Mail, Send, Settings } from "lucide-react";
import { HelpTooltip } from "@/components/HelpTooltip";
import { DevizLibrary, MonthlyDevizeCard } from "./deviz";
import ContractGeneratorDialog from "./contracts/ContractGeneratorDialog";
import OrderBadge, { moveItem } from "./OrderBadge";
import MobileDevizPreview from "./MobileDevizPreview";
import SendOfferDialog from "./deviz/SendOfferDialog";
import CurrencyRelabelMenu from "./deviz/CurrencyRelabelMenu";
import { ContractSelectorDialog, InverseCalcDialog, EditItemDialog, MaterialsPickerDialog } from "./deviz/DevizDialogs";
import { generateDevizPDF, generateDevizPrintHTML, formatDevizNumber } from "@/utils/deviz/pdfGenerator";
import { checkAndDeductCredits } from "@/utils/creditSystem";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DevizTab({ sites = [], companyId, initialEditDevizId = null, editDevizNonce = 0, onDevizOpened }) {
  // View mode: 'library' or 'editor'
  const [viewMode, setViewMode] = useState('library');
  const [editingDevizId, setEditingDevizId] = useState(null);
  
  // Get company name from user data for PDF filename (citim din ambele storage-uri)
  const user = (() => {
    try {
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  })();
  const companyName = user.company_name || 'Firma';
  
  // Biblioteca devize
  const [devizeList, setDevizeList] = useState([]);
  const [loadingDevize, setLoadingDevize] = useState(false);
  
  // Contracte pentru asociere
  const [contractsList, setContractsList] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showContractSelector, setShowContractSelector] = useState(false);
  
  // Generator Contracte din Deviz
  const [showContractFromDeviz, setShowContractFromDeviz] = useState(false);
  const [contractPrefillData, setContractPrefillData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  
  // Calcul invers - preț țintă
  const [showInverseCalc, setShowInverseCalc] = useState(false);
  const [targetPrice, setTargetPrice] = useState('');

  // Monthly devize stats
  const nowDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(nowDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(nowDate.getFullYear());
  const [monthlyDevize, setMonthlyDevize] = useState(null);
  const MONTHS_RO = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];

  // Header info (editable - yellow fields)
  const [devizInfo, setDevizInfo] = useState({
    beneficiar: "",
    beneficiarCui: "",
    executant: "",
    executantCui: "",
    intocmitDe: "",
    intocmitDeTel: "",
    obiectiv: "",
    adresa: "",
    stadiuFizic: "",
    observatii: "",
    numarDeviz: "DEV0001",
    dataDeviz: new Date().toLocaleDateString('ro-RO'),
    // Câmpuri noi pentru asociere contract
    contractId: null,
    contractNumber: "",
    contractDate: ""
  });

  // Work items with quantities - separate material and manopera
  const [workItems, setWorkItems] = useState([
    { 
      id: 1, 
      code: "", 
      name: "Amorsarea suprafetelor cu solutie bituminoasa", 
      um: "mp", 
      quantity: 0, 
      materialPrice: 35,
      manoperaPrice: 15
    }
  ]);

  // Percentages for calculations (defaults: cheltuieli indirecte 4%, profit 5%, TVA 21%)
  const [percentages, setPercentages] = useState({
    contributieMunca: 2.25,
    cheltuieliIndirecte: 4.00,
    profit: 5.00,
    tva: 21.00
  });

  // Calculated totals
  const [totals, setTotals] = useState({
    totalMaterial: 0,
    totalManopera: 0,
    totalDirect: 0,
    contributieMunca: 0,
    t2Material: 0,
    t2Manopera: 0,
    cheltuieliIndirecteMaterial: 0,
    cheltuieliIndirecteManopera: 0,
    t3Material: 0,
    t3Manopera: 0,
    profitMaterial: 0,
    profitManopera: 0,
    t4Material: 0,
    t4Manopera: 0,
    totalFaraTVA: 0,
    tvaValue: 0,
    totalCuTVA: 0
  });

  const [showPreview, setShowPreview] = useState(false);
  // Blob URL for the freshly-generated PDF shown in the preview dialog. When
  // null, the dialog still falls back to the legacy HTML preview.
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  // === Trimitere ofertă pe email beneficiarului ===
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailIncludeMaterials, setEmailIncludeMaterials] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Mobile detection — folosit pentru a evita generarea PDF (cauzează freeze)
  // și a randă în schimb o vizualizare cu cards. Reactiv la resize/rotate.
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobileView(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Currency toggle (RON ↔ EUR) — afișează prețurile + PDF în moneda aleasă.
  // Devizul rămâne salvat în RON; conversia se face doar la display/export.
  const [currency, setCurrency] = useState("RON"); // "RON" sau "EUR"
  const [exchangeRate, setExchangeRate] = useState(4.9750); // RON per 1 EUR
  const [fxLoadedDate, setFxLoadedDate] = useState(null);

  // Încarcă cursul BNR la mount + când userul deschide selectorul
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/fx/bnr`).then((r) => {
      if (!alive) return;
      const eur = r.data?.rates?.EUR;
      if (typeof eur === "number" && eur > 0) {
        setExchangeRate(Number(eur.toFixed(4)));
        setFxLoadedDate(r.data?.date || null);
      }
    }).catch(() => { /* fallback static */ });
    return () => { alive = false; };
  }, []);

  const currencyCode = currency;
  const isEUR = currency === "EUR";
  
  // State pentru editare nume lucrare în dialog
  const [editingItem, setEditingItem] = useState(null);
  const [editingName, setEditingName] = useState("");
  
  // State pentru gestiune materiale (Descarcă Gestiune)
  const [showMaterialsDialog, setShowMaterialsDialog] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [assignedMaterials, setAssignedMaterials] = useState([]);
  const [originalMaterials, setOriginalMaterials] = useState([]); // Materiale originale pentru tracking diferențe
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [materialsSearchTerm, setMaterialsSearchTerm] = useState("");

  // Fetch inventory items
  const fetchInventory = useCallback(async () => {
    if (!companyId) return;
    setLoadingInventory(true);
    try {
      const response = await axios.get(`${API}/inventory`, { params: { company_id: companyId } });
      setInventoryItems(response.data.items || []);
    } catch (error) {
      console.error("Eroare la încărcarea inventarului:", error);
    } finally {
      setLoadingInventory(false);
    }
  }, [companyId]);

  // Fetch company data for contract generator
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/companies/${companyId}`)
      .then(res => setCompanyData(res.data))
      .catch(err => console.error("Error fetching company:", err));
  }, [companyId]);

  // Generare contract din deviz
  const handleGenerateContractFromDeviz = async (deviz) => {
    try {
      const response = await axios.get(`${API}/contracts/prefill-from-deviz/${deviz.id}?company_id=${companyId}`);
      setContractPrefillData(response.data);
      setShowContractFromDeviz(true);
    } catch (error) {
      console.error("Eroare la precompletare contract:", error);
      toast.error("Eroare la generarea contractului din deviz");
    }
  };

  // Add material from inventory to deviz
  const addMaterialToDeviz = (item) => {
    const existing = assignedMaterials.find(m => m.item_id === item.id);
    if (existing) {
      toast.warning("Materialul este deja adăugat");
      return;
    }
    setAssignedMaterials([...assignedMaterials, {
      item_id: item.id,
      item_name: item.name,
      um: item.um,
      material_price: item.material_price,
      quantity: 1,
      stock_available: item.stock
    }]);
    toast.success(`${item.name} adăugat`);
  };

  // Update assigned material quantity
  const updateMaterialQuantity = (itemId, quantity) => {
    setAssignedMaterials(assignedMaterials.map(m => 
      m.item_id === itemId ? { ...m, quantity: parseFloat(quantity) || 0 } : m
    ));
  };

  // Remove material from deviz, restore stock, and auto-save deviz
  const removeMaterialFromDeviz = async (itemId) => {
    const materialToRemove = assignedMaterials.find(m => m.item_id === itemId);
    const newMaterials = assignedMaterials.filter(m => m.item_id !== itemId);
    
    if (materialToRemove && editingDevizId && materialToRemove.quantity > 0) {
      // Deviz existent - restituim stocul și salvăm devizul
      try {
        // 1. Restituim stocul
        await axios.post(`${API}/inventory/bulk-adjust?company_id=${companyId}`, {
          adjustments: [{
            item_id: materialToRemove.item_id,
            quantity: materialToRemove.quantity,
            reason: `Restituire stoc - șters din Deviz ${devizInfo.numarDeviz || editingDevizId}`
          }]
        });
        
        // 2. Salvăm devizul fără materialul șters
        await axios.put(`${API}/devize/${editingDevizId}?company_id=${companyId}`, {
          ...devizInfo,
          workItems,
          percentages,
          totals,
          status: 'final',
          assigned_materials: newMaterials,
          has_materials: newMaterials.length > 0
        });
        
        // 3. Actualizăm originalMaterials pentru tracking corect
        setOriginalMaterials(newMaterials);
        
        toast.success(`${materialToRemove.item_name}: +${materialToRemove.quantity} ${materialToRemove.um} restituit în stoc`);
      } catch (err) {
        console.error("Error removing material:", err);
        toast.error("Eroare la ștergerea materialului");
        return; // Nu eliminăm din UI dacă a eșuat
      }
    }
    
    setAssignedMaterials(newMaterials);
  };

  // Filter inventory for search
  const filteredInventory = inventoryItems.filter(item =>
    item.name?.toLowerCase().includes(materialsSearchTerm.toLowerCase()) ||
    item.code?.toLowerCase().includes(materialsSearchTerm.toLowerCase())
  );

  // Fetch contracte pentru selector
  const fetchContracts = useCallback(async () => {
    if (!companyId) return;
    setLoadingContracts(true);
    try {
      const response = await axios.get(`${API}/documents/contracts/list`, { params: { company_id: companyId } });
      setContractsList(response.data || []);
    } catch (error) {
      console.error("Eroare la încărcarea contractelor:", error);
    } finally {
      setLoadingContracts(false);
    }
  }, [companyId]);

  // Preluare date din contract selectat
  const handleSelectContract = (contract) => {
    setSelectedContract(contract);
    setDevizInfo(prev => ({
      ...prev,
      beneficiar: contract.beneficiar_name || prev.beneficiar,
      beneficiarCui: contract.beneficiar_cui || prev.beneficiarCui,
      obiectiv: contract.obiect_lucrare || prev.obiectiv,
      adresa: contract.locatie_lucrare || prev.adresa,
      contractId: contract.id,
      contractNumber: contract.contract_number || "",
      contractDate: contract.contract_date || ""
    }));
    setShowContractSelector(false);
    toast.success(`Date preluate din contractul CTR ${contract.contract_number}`);
  };
  
  // Mod de ajustare pentru dialog: 'indirecte' | 'preturi' | 'cantitati'
  const [inverseMode, setInverseMode] = useState("indirecte");

  // Calcul invers - ajustare la prețul țintă, cu 3 moduri de aplicare
  const calculateInverseIndirecte = () => {
    const target = parseFloat(targetPrice);
    if (!target || target <= 0) {
      toast.error("Introduceți un preț țintă valid");
      return;
    }
    const currentTotal = totals.totalCuTVA || 0;
    if (currentTotal <= 0) {
      toast.error("Devizul nu are cost — adăugați articole cu prețuri înainte de scalare");
      return;
    }
    const scaleFactor = target / currentTotal;

    // === MODUL 2: Scalează prețurile unitare ===
    if (inverseMode === "preturi") {
      setWorkItems(prev => prev.map(it => ({
        ...it,
        materialPrice: Math.round((parseFloat(it.materialPrice) || 0) * scaleFactor * 100) / 100,
        manoperaPrice: Math.round((parseFloat(it.manoperaPrice) || 0) * scaleFactor * 100) / 100,
      })));
      setShowInverseCalc(false);
      setTargetPrice('');
      toast.success(`Prețuri unitare scalate ×${scaleFactor.toFixed(4)} pentru ${target.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`);
      return;
    }

    // === MODUL 3: Scalează cantitățile ===
    if (inverseMode === "cantitati") {
      setWorkItems(prev => prev.map(it => ({
        ...it,
        quantity: Math.round((parseFloat(it.quantity) || 0) * scaleFactor * 100) / 100,
      })));
      setShowInverseCalc(false);
      setTargetPrice('');
      toast.success(`Cantități scalate ×${scaleFactor.toFixed(4)} pentru ${target.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`);
      return;
    }

    // === MODUL 1 (default): Ajustează % cheltuieli indirecte ===
    const tva = percentages.tva / 100;
    const profit = percentages.profit / 100;
    const contributieMunca = percentages.contributieMunca / 100;

    const totalMaterial = workItems.reduce((sum, item) => sum + (item.quantity * item.materialPrice), 0);
    const totalManopera = workItems.reduce((sum, item) => sum + (item.quantity * item.manoperaPrice), 0);
    const t2 = totalMaterial + totalManopera + (totalManopera * contributieMunca);

    const targetFaraTVA = target / (1 + tva);
    const t3Needed = targetFaraTVA / (1 + profit);
    const indirecteNeeded = (t3Needed / t2) - 1;
    const indirectePercent = indirecteNeeded * 100;

    if (indirectePercent < 0) {
      toast.error(`Prețul țintă (${target.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON) este prea mic pentru costurile directe. Alege modul „Scalează prețuri" sau „Scalează cantități" pentru reducere sub costul direct.`);
      return;
    }

    setPercentages(prev => ({
      ...prev,
      cheltuieliIndirecte: parseFloat(indirectePercent.toFixed(2))
    }));
    setShowInverseCalc(false);
    setTargetPrice('');
    toast.success(`Cheltuieli indirecte ajustate la ${indirectePercent.toFixed(2)}% pentru a atinge ${target.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`);
  };

  // State pentru lookup CUI beneficiar
  const [lookingUpBeneficiarCui, setLookingUpBeneficiarCui] = useState(false);

  // Lookup CUI pentru beneficiar
  const handleLookupBeneficiarCui = async (cui) => {
    if (!cui || cui.length < 2) return;
    
    const cleanCui = cui.replace(/[^0-9]/g, '');
    if (cleanCui.length < 2) return;
    
    setLookingUpBeneficiarCui(true);
    try {
      const response = await axios.get(`${API}/clients/lookup/${cleanCui}?company_id=${companyId}`);
      const { data, source, message } = response.data;
      
      if (data && (data.name || data.denumire)) {
        setDevizInfo(prev => ({
          ...prev,
          beneficiar: data.name || data.denumire || prev.beneficiar,
          beneficiarCui: data.cui || cleanCui,
          adresa: data.address || data.adresa || prev.adresa
        }));
        
        if (source === "local") {
          toast.success("Client găsit în baza de date!");
        } else if (source === "openapi.ro") {
          toast.success("Date firmă preluate automat!");
        }
      } else {
        if (message) {
          toast.info(message);
        } else {
          toast.info("CUI negăsit. Completați manual datele.");
        }
        setDevizInfo(prev => ({ ...prev, beneficiarCui: cleanCui }));
      }
    } catch (error) {
      console.error("CUI lookup error:", error);
      toast.error("Eroare la căutare CUI");
    } finally {
      setLookingUpBeneficiarCui(false);
    }
  };

  // Fetch devize din bibliotecă
  const fetchDevize = useCallback(async () => {
    if (!companyId) return;
    setLoadingDevize(true);
    try {
      const response = await axios.get(`${API}/devize`, { params: { company_id: companyId } });
      setDevizeList(response.data || []);
    } catch (error) {
      console.error("Eroare la încărcarea devizelor:", error);
    } finally {
      setLoadingDevize(false);
    }
  }, [companyId]);

  // Load devize la mount
  useEffect(() => {
    fetchDevize();
  }, [fetchDevize]);

  // Monthly devize stats
  const fetchMonthlyDevize = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/devize/monthly-stats`, {
        params: { company_id: companyId, year: selectedYear, month: selectedMonth }
      });
      setMonthlyDevize(res.data);
    } catch (e) { console.error("Monthly devize fetch error:", e); }
  }, [companyId, selectedYear, selectedMonth]);

  useEffect(() => {
    if (companyId) fetchMonthlyDevize();
  }, [fetchMonthlyDevize, companyId]);

  const handleMonthPrev = () => {
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(y => y - 1); }
    else setSelectedMonth(m => m - 1);
  };
  const handleMonthNext = () => {
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(y => y + 1); }
    else setSelectedMonth(m => m + 1);
  };


  // State pentru auto-save indicator
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);

  // ── Setări Deviz: culoare brand pentru sigla text din PDF ───────────
  const DEFAULT_BRAND_COLOR = "#4CAF50";
  const [brandColor, setBrandColor] = useState(() => {
    try {
      if (companyId) {
        const saved = localStorage.getItem(`deviz_brand_color_${companyId}`);
        if (saved) return saved;
      }
    } catch (e) {
      console.warn("brandColor init: localStorage error", e);
    }
    return DEFAULT_BRAND_COLOR;
  });
  const handleBrandColorChange = (color) => {
    setBrandColor(color);
    if (companyId) localStorage.setItem(`deviz_brand_color_${companyId}`, color);
  };

  // Auto-save ciornă la fiecare modificare (debounced) pentru deviz NOU
  useEffect(() => {
    if (editingDevizId) return; // Nu salvăm local dacă editam un deviz existent
    
    const draftKey = `deviz_draft_${companyId}`;
    const saveDraft = () => {
      const draft = {
        devizInfo,
        workItems,
        percentages,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(draftKey, JSON.stringify(draft));
    };
    
    // Save draft after 2 seconds of inactivity
    const timer = setTimeout(saveDraft, 2000);
    return () => clearTimeout(timer);
  }, [devizInfo, workItems, percentages, companyId, editingDevizId]);

  // Auto-save la server pentru deviz EXISTENT (la fiecare modificare, debounced)
  useEffect(() => {
    if (!editingDevizId || viewMode !== 'editor' || !companyId) return;
    
    const autoSaveToServer = async () => {
      setAutoSaving(true);
      try {
        const devizData = {
          ...devizInfo,
          workItems,
          percentages,
          totals,
          status: 'final',
          assigned_materials: assignedMaterials,
          has_materials: assignedMaterials.length > 0
        };
        
        await axios.put(`${API}/devize/${editingDevizId}?company_id=${companyId}`, devizData);
        setLastAutoSave(new Date());
        // Nu afișăm toast la fiecare auto-save pentru a nu deranja utilizatorul
      } catch (error) {
        console.error("Auto-save error:", error);
        // Afișăm eroare doar dacă salvarea eșuează
        toast.error("Eroare la auto-salvare", { description: "Modificările nu au fost salvate automat." });
      } finally {
        setAutoSaving(false);
      }
    };
    
    // Auto-save după 3 secunde de inactivitate
    const timer = setTimeout(autoSaveToServer, 3000);
    return () => clearTimeout(timer);
  }, [devizInfo, workItems, percentages, editingDevizId, viewMode, assignedMaterials, totals, companyId]);

  // Restore draft on mount (dacă suntem în editor și nu editam un deviz existent)
  useEffect(() => {
    if (viewMode === 'editor' && !editingDevizId) {
      const draftKey = `deviz_draft_${companyId}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          const savedTime = new Date(draft.savedAt);
          const hoursSinceSave = (Date.now() - savedTime.getTime()) / (1000 * 60 * 60);
          
          // Restaurează doar dacă ciorna e mai recentă de 24 ore
          if (hoursSinceSave < 24 && draft.workItems?.length > 0) {
            setDevizInfo(draft.devizInfo);
            setWorkItems(draft.workItems);
            setPercentages(draft.percentages);
            toast.info("Ciornă restaurată", { description: "Am restaurat devizul nesalvat." });
          }
        } catch (e) {
          console.error("Eroare la restaurarea ciornei:", e);
        }
      }
    }
  }, [viewMode, editingDevizId, companyId]);

  // Clear draft after successful save
  const clearDraft = () => {
    const draftKey = `deviz_draft_${companyId}`;
    localStorage.removeItem(draftKey);
  };

  // Calculate totals whenever workItems or percentages change
  useEffect(() => {
    calculateTotals();
  }, [workItems, percentages]);

  const calculateTotals = () => {
    // Calculate direct costs
    const totalMaterial = workItems.reduce((sum, item) => sum + (item.quantity * item.materialPrice), 0);
    const totalManopera = workItems.reduce((sum, item) => sum + (item.quantity * item.manoperaPrice), 0);
    const totalDirect = totalMaterial + totalManopera;

    // Contributie munca (only on manopera)
    const contributieMunca = totalManopera * (percentages.contributieMunca / 100);
    
    // T2 = T1 + Alte cheltuieli directe
    const t2Material = totalMaterial;
    const t2Manopera = totalManopera + contributieMunca;

    // Cheltuieli indirecte
    const cheltuieliIndirecteMaterial = t2Material * (percentages.cheltuieliIndirecte / 100);
    const cheltuieliIndirecteManopera = t2Manopera * (percentages.cheltuieliIndirecte / 100);

    // T3 = T2 + Cheltuieli indirecte
    const t3Material = t2Material + cheltuieliIndirecteMaterial;
    const t3Manopera = t2Manopera + cheltuieliIndirecteManopera;

    // Profit (Beneficiu)
    const profitMaterial = t3Material * (percentages.profit / 100);
    const profitManopera = t3Manopera * (percentages.profit / 100);

    // T4 = T3 + Beneficiu
    const t4Material = t3Material + profitMaterial;
    const t4Manopera = t3Manopera + profitManopera;

    // Total fara TVA
    const totalFaraTVA = t4Material + t4Manopera;

    // TVA
    const tvaValue = totalFaraTVA * (percentages.tva / 100);

    // Total cu TVA
    const totalCuTVA = totalFaraTVA + tvaValue;

    setTotals({
      totalMaterial,
      totalManopera,
      totalDirect,
      contributieMunca,
      t2Material,
      t2Manopera,
      cheltuieliIndirecteMaterial,
      cheltuieliIndirecteManopera,
      t3Material,
      t3Manopera,
      profitMaterial,
      profitManopera,
      t4Material,
      t4Manopera,
      totalFaraTVA,
      tvaValue,
      totalCuTVA
    });
  };

  const addWorkItem = () => {
    const newId = Math.max(...workItems.map(w => w.id), 0) + 1;
    setWorkItems([...workItems, {
      id: newId,
      code: "",
      name: "",
      um: "mp",
      quantity: 0,
      materialPrice: 0,
      manoperaPrice: 0,
      materials: [] // NEW: materials per work item
    }]);
  };

  const removeWorkItem = (id) => {
    if (workItems.length > 1) {
      setWorkItems(workItems.filter(w => w.id !== id));
    }
  };

  const updateWorkItem = (id, field, value) => {
    setWorkItems(workItems.map(w => 
      w.id === id ? { ...w, [field]: value } : w
    ));
  };

  // === Material per work item functions ===
  const addMaterialToItem = (itemId) => {
    setWorkItems(workItems.map(w => {
      if (w.id !== itemId) return w;
      const matId = Date.now() + Math.random();
      return { ...w, materials: [...(w.materials || []), { 
        matId, inventoryItemId: null, name: "", um: "buc", quantity: 0, price: 0, fromInventory: false, autoAdded: false 
      }]};
    }));
  };

  const updateItemMaterial = (itemId, matId, field, value) => {
    setWorkItems(workItems.map(w => {
      if (w.id !== itemId) return w;
      return { ...w, materials: (w.materials || []).map(m => 
        m.matId === matId ? { ...m, [field]: value } : m
      )};
    }));
  };

  const removeItemMaterial = (itemId, matId) => {
    setWorkItems(workItems.map(w => {
      if (w.id !== itemId) return w;
      return { ...w, materials: (w.materials || []).filter(m => m.matId !== matId) };
    }));
  };

  const selectInventoryForMaterial = (itemId, matId, invItem) => {
    setWorkItems(workItems.map(w => {
      if (w.id !== itemId) return w;
      return { ...w, materials: (w.materials || []).map(m => 
        m.matId === matId ? { 
          ...m, 
          inventoryItemId: invItem.id, 
          name: invItem.name, 
          um: invItem.um || "buc", 
          price: invItem.material_price || 0, 
          fromInventory: true,
          autoAdded: false
        } : m
      )};
    }));
  };

  const addNewMaterialToInventory = async (itemId, matId, materialName, um, price) => {
    try {
      const response = await axios.post(`${API}/inventory`, {
        name: materialName,
        um: um || "buc",
        material_price: price || 0,
        manopera_price: 0,
        stock: 0,
        supplier: "",
        category: "auto-deviz",
        code: "AD-" + Date.now().toString().slice(-6)
      }, { params: { company_id: companyId } });
      
      updateItemMaterial(itemId, matId, 'inventoryItemId', response.data?.id || null);
      updateItemMaterial(itemId, matId, 'fromInventory', true);
      updateItemMaterial(itemId, matId, 'autoAdded', true);
      toast.success(`"${materialName}" adăugat automat în inventar (categorie: auto-deviz)`);
    } catch (error) {
      toast.error("Eroare la adăugarea în inventar");
    }
  };

  const handleSaveDeviz = async () => {
    const isNewDeviz = !editingDevizId;
    const actionCode = isNewDeviz ? 'generate_deviz' : 'edit_deviz';
    
    // Verifică și deduce creditele
    const creditResult = await checkAndDeductCredits(
      companyId,
      actionCode,
      null,
      (warning) => toast.warning(warning, { duration: 8000 }),
      (block) => toast.error(block, { duration: 8000 })
    );
    
    if (!creditResult.canProceed) {
      return; // Blocat - credite insuficiente și bonus folosit
    }
    
    if (creditResult.usedBonus) {
      toast.warning("⚠️ Ați folosit documentul bonus gratuit!", { duration: 5000 });
    }
    
    try {
      const devizData = {
        ...devizInfo,
        workItems,
        percentages,
        totals,
        status: 'final',
        assigned_materials: assignedMaterials,
        has_materials: assignedMaterials.length > 0
      };
      
      let devizId = editingDevizId;
      
      if (editingDevizId) {
        // Update existent
        await axios.put(`${API}/devize/${editingDevizId}?company_id=${companyId}`, devizData);
        toast.success("Deviz actualizat cu succes!");
      } else {
        // Creare nou
        const response = await axios.post(`${API}/devize?company_id=${companyId}`, devizData);
        devizId = response.data.id;
        toast.success("Deviz salvat cu succes!");
      }
      
      // Calculăm diferențele de stoc
      if (devizId) {
        const adjustments = [];
        
        if (isNewDeviz) {
          // Deviz nou - descărcăm tot stocul pentru materialele noi
          assignedMaterials.forEach(mat => {
            if (mat.quantity > 0) {
              adjustments.push({
                item_id: mat.item_id,
                quantity: -mat.quantity,
                reason: `Descărcare gestiune - Deviz ${devizInfo.numarDeviz || devizId}`
              });
            }
          });
        } else {
          // Deviz existent - calculăm diferențele
          // Materiale noi sau cu cantitate mărită
          assignedMaterials.forEach(mat => {
            const originalMat = originalMaterials.find(o => o.item_id === mat.item_id);
            const originalQty = originalMat?.quantity || 0;
            const diff = mat.quantity - originalQty;
            
            if (diff !== 0) {
              adjustments.push({
                item_id: mat.item_id,
                quantity: -diff, // Negativ dacă am adăugat mai mult, pozitiv dacă am redus
                reason: `Ajustare gestiune - Deviz ${devizInfo.numarDeviz || devizId}`
              });
            }
          });
          
          // Materiale șterse complet (deja gestionate în removeMaterialFromDeviz)
          // Nu mai trebuie să le procesăm aici
        }
        
        if (adjustments.length > 0) {
          try {
            await axios.post(`${API}/inventory/bulk-adjust?company_id=${companyId}`, {
              adjustments
            });
            toast.success(`Stoc actualizat pentru ${adjustments.length} produse`);
          } catch (stockErr) {
            console.error("Stock adjustment error:", stockErr);
            toast.warning("Deviz salvat, dar stocul nu a fost actualizat complet");
          }
        }
      }
      
      clearDraft();
      fetchDevize();
      setViewMode('library');
      resetForm();
    } catch (error) {
      toast.error("Eroare la salvare deviz");
    }
  };

  const handleDeleteDeviz = async (devizId) => {
    if (!confirm("Sigur doriți să ștergeți acest deviz? Materialele vor fi restituite în stoc.")) return;
    
    try {
      // First, get the deviz to check for materials
      const devizToDelete = devizeList.find(d => d.id === devizId);
      
      // Restore materials to inventory if any
      if (devizToDelete?.assigned_materials?.length > 0) {
        try {
          const adjustments = devizToDelete.assigned_materials.map(mat => ({
            item_id: mat.item_id,
            quantity: mat.quantity,  // Pozitiv = restituire în stoc
            reason: `Restituire stoc - Deviz ${devizToDelete.numarDeviz || devizId} șters`
          }));
          
          await axios.post(`${API}/inventory/bulk-adjust?company_id=${companyId}`, {
            adjustments
          });
          
          toast.success(`${devizToDelete.assigned_materials.length} produse restituite în stoc`);
        } catch (stockErr) {
          console.error("Stock restore error:", stockErr);
          toast.warning("Deviz șters, dar stocul nu a fost actualizat");
        }
      }
      
      await axios.delete(`${API}/devize/${devizId}?company_id=${companyId}`);
      toast.success("Deviz șters!");
      fetchDevize();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleDuplicateDeviz = async (deviz) => {
    try {
      const response = await axios.post(`${API}/devize/${deviz.id}/duplicate?company_id=${companyId}`);
      const newDeviz = response.data.deviz;
      toast.success(`Copie creată: ${newDeviz.numar_deviz || newDeviz.numarDeviz}`, { 
        description: "Completează beneficiarul și datele, apoi salvează." 
      });
      fetchDevize();
      // Deschidem editorul cu noul deviz
      handleEditDeviz(newDeviz);
    } catch (error) {
      console.error("Eroare la duplicare:", error);
      // Afișează mesajul real venit de la server (proformă blocată / credite insuficiente / etc.)
      const detail = error?.response?.data?.detail;
      let msg = "Eroare la duplicarea devizului";
      let desc = null;
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        msg = detail.message || msg;
        if (detail.blocked_by_proforma) {
          desc = `Achită proforma ${detail.proforma_number} (${Number(detail.proforma_total || 0).toFixed(2)} RON) și reîncearcă.`;
        } else if (detail.required != null && detail.available != null) {
          desc = `Necesare: ${detail.required} credite · Disponibile: ${detail.available}.`;
        }
      }
      toast.error(msg, desc ? { description: desc } : undefined);
    }
  };


  const handleEditDeviz = async (deviz) => {
    setEditingDevizId(deviz.id);
    setDevizInfo({
      beneficiar: deviz.beneficiar || "",
      beneficiarCui: deviz.beneficiarCui || "",
      executant: deviz.executant || "",
      executantCui: deviz.executantCui || "",
      intocmitDe: deviz.intocmitDe || "",
      intocmitDeTel: deviz.intocmitDeTel || "",
      obiectiv: deviz.obiectiv || "",
      adresa: deviz.adresa || "",
      stadiuFizic: deviz.stadiuFizic || "",
      observatii: deviz.observatii || "",
      numarDeviz: deviz.numar_deviz || deviz.numarDeviz || "DEV0001",
      dataDeviz: deviz.dataDeviz || new Date().toLocaleDateString('ro-RO'),
      contractId: deviz.contractId || null,
      contractNumber: deviz.contractNumber || "",
      contractDate: deviz.contractDate || ""
    });
    setWorkItems(deviz.workItems || [{ id: 1, code: "", name: "", um: "mp", quantity: 0, materialPrice: 0, manoperaPrice: 0 }]);
    setPercentages(deviz.percentages || { contributieMunca: 2.25, cheltuieliIndirecte: 4.00, profit: 5.00, tva: 21.00 });
    const materials = deviz.assigned_materials || [];
    setAssignedMaterials(materials);
    setOriginalMaterials(JSON.parse(JSON.stringify(materials))); // Deep copy pentru comparație
    setLastAutoSave(null); // Reset auto-save indicator
    setSelectedContract(null);
    setViewMode('editor');
    // Preîncărcăm inventarul și contractele pentru sugestii
    fetchInventory();
    fetchContracts();
  };

  // Deschide automat un deviz cerut din alt tab (ex: Contract → Deschide în DevizePRO)
  useEffect(() => {
    if (!initialEditDevizId || !companyId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/devize/${initialEditDevizId}?company_id=${companyId}`);
        if (res.data) {
          handleEditDeviz(res.data);
          onDevizOpened && onDevizOpened();
        }
      } catch (e) {
        console.error("Nu am putut deschide devizul:", e);
        toast.error("Devizul solicitat nu a putut fi deschis");
      }
    })();
    // eslint-disable-next-line
  }, [initialEditDevizId, editDevizNonce, companyId]);

  const handleViewDeviz = (deviz) => {
    handleEditDeviz(deviz);
    // Pe mobil: NU generăm PDF (jsPDF freezuieză telefonul și iframe-ul nu randa fiabil).
    // Deschidem direct dialogul cu preview simplificat React (cards mobile-first).
    if (isMobileView) {
      setTimeout(() => {
        if (pdfPreviewUrl) {
          try { URL.revokeObjectURL(pdfPreviewUrl); } catch { /* ignore */ }
          setPdfPreviewUrl(null);
        }
        setShowPreview(true);
      }, 200);
      return;
    }
    // Desktop: PDF-ul rămâne ca înainte
    setTimeout(() => handleGeneratePDF(false, 'view'), 250);
  };

  const resetForm = () => {
    setEditingDevizId(null);
    setSelectedContract(null);
    setDevizInfo({
      beneficiar: "",
      beneficiarCui: "",
      executant: "",
      executantCui: "",
      intocmitDe: "",
      intocmitDeTel: "",
      obiectiv: "",
      adresa: "",
      stadiuFizic: "",
      observatii: "",
      numarDeviz: "DEV0001",
      dataDeviz: new Date().toLocaleDateString('ro-RO'),
      contractId: null,
      contractNumber: "",
      contractDate: ""
    });
    setWorkItems([{ id: 1, code: "", name: "", um: "mp", quantity: 0, materialPrice: 0, manoperaPrice: 0 }]);
    setPercentages({ contributieMunca: 2.25, cheltuieliIndirecte: 4.00, profit: 5.00, tva: 21.00 });
    setAssignedMaterials([]);
    setOriginalMaterials([]);
    setLastAutoSave(null);
  };

  const startNewDeviz = () => {
    resetForm();
    setViewMode('editor');
    // Preîncărcăm inventarul și contractele pentru sugestii
    fetchInventory();
    fetchContracts();
  };

  const handlePrint = async () => {
    // Use the same PDF format as "Descarcă PDF" — open the generated PDF in a
    // new window and trigger the browser's print dialog on it.
    await handleGeneratePDF(false, 'print');
  };

  // === Trimitere ofertă pe email ===
  const openSendOfferDialog = () => {
    if (!editingDevizId) {
      toast.warning("Salvează mai întâi devizul înainte de a trimite oferta.");
      return;
    }
    // Prefill destinatar: încercăm să-l ghicim din câmpul beneficiar dacă conține un email
    const guess = (devizInfo.beneficiar || "").match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    setEmailRecipient(guess ? guess[0] : "");
    setEmailSubject(`Ofertă deviz ${devizInfo.numarDeviz} — ${devizInfo.executant || companyName}`);
    setEmailMessage(
      `Vă transmitem oferta solicitată pentru ${devizInfo.obiectiv || 'lucrările discutate'}.\n\n` +
      `Documentul atașat conține detalierea capitolelor de lucrări, prețurile materialelor și manopera.\n\n` +
      `Pentru orice modificări sau întrebări, ne puteți contacta direct.`
    );
    setEmailIncludeMaterials(false);
    setShowEmailDialog(true);
  };

  const handleSendOffer = async () => {
    const email = (emailRecipient || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Introduceți o adresă de email validă pentru destinatar.");
      return;
    }
    setSendingEmail(true);
    try {
      // 1. Generăm PDF-ul în format base64 (același flow ca "Descarcă PDF")
      const payload = await handleGeneratePDF(emailIncludeMaterials, 'email');
      if (!payload || !payload.base64) {
        toast.error("Nu am putut genera PDF-ul devizului.");
        return;
      }
      // 2. POST către backend (Resend trimite emailul cu PDF atașat)
      const res = await axios.post(`${API}/devize/${editingDevizId}/send-offer`, {
        recipient_email: email,
        recipient_name: devizInfo.beneficiar || "",
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
        pdf_base64: payload.base64,
        filename: payload.filename,
      }, { params: { company_id: companyId } });
      toast.success(res.data?.message || "Oferta a fost trimisă cu succes.");
      setShowEmailDialog(false);
    } catch (err) {
      console.error("Eroare la trimiterea ofertei:", err);
      const msg = err?.response?.data?.detail || "Nu am putut trimite oferta. Verifică furnizorul de email.";
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  // Wrapper-uri către modulul extras `utils/deviz/pdfGenerator.js`.
  // Trec state-ul curent ca params (devizInfo, workItems, totals, percentages,
  // currency, exchangeRate, isEUR) — funcțiile rămân pure în modulul extras.
  const formatNumber = (num) => formatDevizNumber(num, { isEUR, exchangeRate });

  const handleGeneratePDF = (includeMaterials = false, mode = 'save') =>
    generateDevizPDF({
      devizInfo,
      workItems,
      totals,
      percentages,
      currencyCode,
      isEUR,
      exchangeRate,
      includeMaterials,
      mode,
      companyLogoUrl: companyData?.logo_url || "",
      companyName: companyData?.company_name || companyData?.name || devizInfo.executant || "",
      brandColor,
      onPreview: (url) => { setPdfPreviewUrl(url); setShowPreview(true); },
      notify: (kind, msg) => toast[kind](msg),
    });

  const generatePrintHTML = () =>
    generateDevizPrintHTML({
      devizInfo,
      workItems,
      totals,
      percentages,
      currencyCode,
      isEUR,
      exchangeRate,
    });

  // Render Library View - Using extracted DevizLibrary component
  if (viewMode === 'library') {
    return (
      <div className="space-y-6">
        {/* Monthly Devize Total Card */}
        <MonthlyDevizeCard
          monthlyDevize={monthlyDevize}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          setSelectedYear={setSelectedYear}
          setSelectedMonth={setSelectedMonth}
          onMonthPrev={handleMonthPrev}
          onMonthNext={handleMonthNext}
        />

        <DevizLibrary
          devizeList={devizeList}
          loadingDevize={loadingDevize}
          companyId={companyId}
          onNewDeviz={startNewDeviz}
          onEditDeviz={handleEditDeviz}
          onViewDeviz={handleViewDeviz}
          onDeleteDeviz={handleDeleteDeviz}
          onDuplicateDeviz={handleDuplicateDeviz}
          onGenerateContract={handleGenerateContractFromDeviz}
          HelpTooltip={HelpTooltip}
        />

        {/* Contract Generator Dialog - triggered from deviz */}
        <ContractGeneratorDialog
          open={showContractFromDeviz}
          onOpenChange={(open) => {
            setShowContractFromDeviz(open);
            if (!open) setContractPrefillData(null);
          }}
          companyId={companyId}
          companyData={companyData}
          prefillFromDeviz={contractPrefillData}
          onRefresh={fetchDevize}
        />
      </div>
    );
  }

  // Render Editor View
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setViewMode('library'); resetForm(); }}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                {editingDevizId ? 'Editare Deviz' : 'DEVIZ OFERTĂ NOU'}
                <HelpTooltip section="deviz" />
              </CardTitle>
              <CardDescription className="text-base font-medium mt-1">
                {devizInfo.numarDeviz} din {devizInfo.dataDeviz}
                {!editingDevizId && <span className="text-orange-500 ml-2">(Auto-salvare ciornă activă)</span>}
                {editingDevizId && (
                  <span className="ml-2 flex items-center gap-1 inline-flex">
                    {autoSaving ? (
                      <span className="text-blue-500 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Se salvează...
                      </span>
                    ) : lastAutoSave ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Salvat la {lastAutoSave.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : (
                      <span className="text-slate-400">(Auto-salvare activă)</span>
                    )}
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="default" onClick={() => handleGeneratePDF(false, 'view')} className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400">
              <Eye className="w-5 h-5 mr-2" />
              Previzualizare
            </Button>
            <Button variant="outline" size="default" onClick={handlePrint} className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400">
              <Printer className="w-5 h-5 mr-2" />
              Printează
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="default" className="bg-teal-50 border-teal-300 text-teal-700 hover:bg-teal-100 hover:border-teal-400">
                  <Download className="w-5 h-5 mr-2" />
                  Descarcă PDF
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleGeneratePDF(false)}>
                  <FileText className="w-4 h-4 mr-2" />
                  PDF Simplu
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleGeneratePDF(true)}>
                  <Package className="w-4 h-4 mr-2" />
                  PDF Inclus Materiale
                  {workItems.some(item => item.materials && item.materials.length > 0) && (
                    <Badge variant="secondary" className="ml-2 text-xs bg-amber-100 text-amber-700">
                      {workItems.reduce((sum, item) => sum + (item.materials?.length || 0), 0)}
                    </Badge>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="default"
              onClick={openSendOfferDialog}
              disabled={!editingDevizId}
              className="bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-400 disabled:opacity-50"
              data-testid="send-offer-email-btn"
              title={editingDevizId ? "Trimite oferta pe email beneficiarului" : "Salvează devizul mai întâi"}
            >
              <Mail className="w-5 h-5 mr-2" />
              Trimite pe Email
            </Button>
            <Button variant="outline" size="default" onClick={() => { fetchInventory(); setShowMaterialsDialog(true); }} className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100 hover:border-amber-400">
              <Package className="w-5 h-5 mr-2" />
              Gestiune ({assignedMaterials.length})
            </Button>
            <Button onClick={handleSaveDeviz} size="default" className="bg-green-600 hover:bg-green-700">
              <Save className="w-5 h-5 mr-2" />
              {editingDevizId ? 'Actualizează' : 'Salvează'}
            </Button>
            {/* Setări Deviz (popover în colț) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="default"
                  title="Setări deviz"
                  className="bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"
                  data-testid="deviz-settings-btn"
                >
                  <Settings className="w-5 h-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-sm">Setări Deviz</h4>
                    <p className="text-xs text-slate-500">Personalizează aspectul PDF-ului</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Culoare brand (siglă text)</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={brandColor}
                        onChange={(e) => handleBrandColorChange(e.target.value)}
                        className="w-12 h-9 rounded border border-slate-200 cursor-pointer p-0"
                        data-testid="brand-color-picker"
                      />
                      <span className="text-xs font-mono text-slate-600">{brandColor.toUpperCase()}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleBrandColorChange(DEFAULT_BRAND_COLOR)}
                        className="ml-auto text-xs h-7"
                        data-testid="brand-color-reset-btn"
                      >
                        Reset
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500 leading-snug">
                      Se aplică pe sigla text (dacă firma n-are logo), pe barele secțiunilor și pe bara &laquo;TOTAL GENERAL&raquo;.
                    </p>
                  </div>
                  {/* Previzualizare siglă */}
                  <div className="flex items-center gap-3 pt-2 border-t">
                    {(() => {
                      // Calculează luminanța pentru a alege text alb sau negru
                      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(brandColor || "");
                      const r = m ? parseInt(m[1], 16) : 76;
                      const g = m ? parseInt(m[2], 16) : 175;
                      const b = m ? parseInt(m[3], 16) : 80;
                      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
                      const textCol = lum > 165 ? "#1e1e1e" : "#ffffff";
                      const raw = (companyData?.company_name || companyData?.name || devizInfo.executant || "")
                        .replace(/\b(SRL|SA|SRL-D|PFA|SC|S\.R\.L\.|S\.A\.)\b/gi, "")
                        .replace(/[^\p{L}\p{N}\s-]/gu, "")
                        .trim();
                      const parts = raw.split(/\s+/).filter(Boolean);
                      let initials = "ET";
                      if (parts.length === 1) initials = parts[0].substring(0, 3).toUpperCase();
                      else if (parts.length > 1) initials = parts.slice(0, 3).map((p) => p[0] || "").join("").toUpperCase();
                      return (
                        <div
                          className="w-14 h-10 rounded-md flex items-center justify-center font-bold text-sm shadow-sm"
                          style={{ backgroundColor: brandColor, color: textCol }}
                          data-testid="brand-sigla-preview"
                        >
                          {initials}
                        </div>
                      );
                    })()}
                    <span className="text-xs text-slate-500">Sigla + bara secțiuni + bara Total</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          {/* Header Info Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="space-y-3">
              <div>
                <Label>Beneficiar *</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input 
                    value={devizInfo.beneficiar}
                    onChange={(e) => setDevizInfo({...devizInfo, beneficiar: e.target.value})}
                    className="col-span-2 bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                    placeholder="Numele beneficiarului"
                  />
                  <div className="relative">
                    <Input 
                      value={devizInfo.beneficiarCui}
                      onChange={(e) => setDevizInfo({...devizInfo, beneficiarCui: e.target.value})}
                      onBlur={(e) => e.target.value && handleLookupBeneficiarCui(e.target.value)}
                      className="bg-yellow-100 border-yellow-300 focus:border-yellow-500 pr-8"
                      placeholder="CUI"
                    />
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleLookupBeneficiarCui(devizInfo.beneficiarCui)}
                      disabled={lookingUpBeneficiarCui || !devizInfo.beneficiarCui}
                      className="absolute right-0 top-0 h-full px-2 hover:bg-yellow-200"
                      title="Caută firmă după CUI"
                    >
                      {lookingUpBeneficiarCui ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <div>
                <Label>Executant *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    value={devizInfo.executant}
                    onChange={(e) => setDevizInfo({...devizInfo, executant: e.target.value})}
                    className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                    placeholder="Nume executant"
                  />
                  <Input 
                    value={devizInfo.executantCui}
                    onChange={(e) => setDevizInfo({...devizInfo, executantCui: e.target.value})}
                    className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                    placeholder="CUI: RO12345678"
                  />
                </div>
              </div>
              <div>
                <Label>Întocmit de:</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    value={devizInfo.intocmitDe}
                    onChange={(e) => setDevizInfo({...devizInfo, intocmitDe: e.target.value})}
                    className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                    placeholder="Nume și prenume"
                  />
                  <Input 
                    value={devizInfo.intocmitDeTel}
                    onChange={(e) => setDevizInfo({...devizInfo, intocmitDeTel: e.target.value})}
                    className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                    placeholder="Tel: 0755 555 555"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Obiectivul</Label>
                <Input 
                  value={devizInfo.obiectiv}
                  onChange={(e) => setDevizInfo({...devizInfo, obiectiv: e.target.value})}
                  className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                  placeholder="Descriere obiectiv"
                />
              </div>
              <div>
                <Label>Adresa</Label>
                <Input 
                  value={devizInfo.adresa}
                  onChange={(e) => setDevizInfo({...devizInfo, adresa: e.target.value})}
                  className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                  placeholder="Adresa lucrării"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Nr. Deviz</Label>
                  <Input 
                    value={devizInfo.numarDeviz}
                    onChange={(e) => setDevizInfo({...devizInfo, numarDeviz: e.target.value})}
                    className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                  />
                </div>
                <div>
                  <Label>Data</Label>
                  <Input 
                    value={devizInfo.dataDeviz}
                    onChange={(e) => setDevizInfo({...devizInfo, dataDeviz: e.target.value})}
                    className="bg-yellow-100 border-yellow-300 focus:border-yellow-500"
                  />
                </div>
              </div>

              {/* Observații — apar pe PDF sub semnături (doar dacă sunt completate) */}
              <div className="mt-2">
                <Label>Observații</Label>
                <Textarea
                  value={devizInfo.observatii || ""}
                  onChange={(e) => setDevizInfo({...devizInfo, observatii: e.target.value})}
                  className="bg-yellow-100 border-yellow-300 focus:border-yellow-500 min-h-[80px]"
                  placeholder="Notițe, mențiuni speciale, condiții... (apar pe PDF sub semnături — opțional)"
                  data-testid="deviz-observatii-input"
                />
              </div>
              
              {/* Selector Contract */}
              <div className="mt-2">
                <Label className="flex items-center gap-1">
                  <Link className="w-3 h-3" />
                  Asociat Contractului
                </Label>
                {devizInfo.contractNumber ? (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 p-2 bg-green-100 border border-green-300 rounded-md text-sm text-green-800">
                      <strong>CTR {devizInfo.contractNumber}</strong> din {devizInfo.contractDate}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setDevizInfo({...devizInfo, contractId: null, contractNumber: '', contractDate: ''})}
                      className="text-red-500 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { fetchContracts(); setShowContractSelector(true); }}
                    className="w-full mt-1 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Preia date din Contract
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Work Items Table - Responsive cu zoom pe mobile */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2">
              <h3 className="font-semibold text-base sm:text-lg">SECȚIUNEA TEHNICĂ - Capitole de lucrări</h3>
              <Button onClick={addWorkItem} size="sm" variant="outline" className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-1" />
                Adaugă Lucrare
              </Button>
            </div>
            
            {/* Mobile Card View */}
            <div className="block sm:hidden space-y-3">
              {workItems.map((item, index) => (
                <div key={item.id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="flex items-start justify-between mb-2">
                    <OrderBadge
                      index={index}
                      total={workItems.length}
                      onReorder={(from, to) => setWorkItems((arr) => moveItem(arr, from, to))}
                      variant="mobile"
                      testIdPrefix="deviz-order-mobile"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeWorkItem(item.id)}
                      className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Denumire lucrare */}
                  <div 
                    className="bg-white p-2 rounded border border-slate-200 mb-3 cursor-pointer hover:bg-yellow-50"
                    onClick={() => {
                      setEditingItem(item);
                      setEditingName(item.name);
                    }}
                  >
                    <p className="text-xs text-slate-500 mb-1">Capitol de lucrări:</p>
                    <p className="text-sm font-medium">
                      {item.name || <span className="text-slate-400 italic">Click pentru a edita...</span>}
                    </p>
                  </div>
                  
                  {/* Grid cu valorile */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">U.M.</Label>
                      <Select 
                        value={item.um} 
                        onValueChange={(v) => updateWorkItem(item.id, 'um', v)}
                      >
                        <SelectTrigger className="h-9 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mp">mp</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="mc">mc</SelectItem>
                          <SelectItem value="buc">buc</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="ora">ora</SelectItem>
                          <SelectItem value="to">to</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Cantitate</Label>
                      <Input
                        type="number"
                        value={item.quantity === 0 || item.quantity === '' || item.quantity == null ? '' : item.quantity}
                        onChange={(e) => updateWorkItem(item.id, 'quantity', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                        className="h-9 bg-yellow-100 border-yellow-300"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Preț Material</Label>
                      <Input
                        type="number"
                        value={item.materialPrice === 0 || item.materialPrice === '' || item.materialPrice == null ? '' : item.materialPrice}
                        onChange={(e) => updateWorkItem(item.id, 'materialPrice', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                        className="h-9 bg-yellow-100 border-yellow-300"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Preț Manoperă</Label>
                      <Input
                        type="number"
                        value={item.manoperaPrice === 0 || item.manoperaPrice === '' || item.manoperaPrice == null ? '' : item.manoperaPrice}
                        onChange={(e) => updateWorkItem(item.id, 'manoperaPrice', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                        className="h-9 bg-yellow-100 border-yellow-300"
                      />
                    </div>
                  </div>
                  
                  {/* Totaluri */}
                  <div className="mt-3 pt-2 border-t border-slate-200 grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <p className="text-slate-500">Material</p>
                      <p className="font-semibold">{formatNumber(item.quantity * item.materialPrice)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-slate-500">Manoperă</p>
                      <p className="font-semibold">{formatNumber(item.quantity * item.manoperaPrice)}</p>
                    </div>
                    <div className="text-center bg-green-100 rounded p-1">
                      <p className="text-slate-500">Total</p>
                      <p className="font-bold text-green-700">{formatNumber(item.quantity * (item.materialPrice + item.manoperaPrice))}</p>
                    </div>
                  </div>

                  {/* Materiale per lucrare */}
                  <div className="mt-3 pt-2 border-t border-blue-200 bg-blue-50 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-blue-700 flex items-center gap-1"><Package className="w-3 h-3" /> Materiale consumate</p>
                      <Button size="sm" variant="ghost" onClick={() => addMaterialToItem(item.id)} className="h-6 text-blue-600 hover:text-blue-800 text-xs p-1" data-testid={`add-material-btn-${item.id}`}>
                        <Plus className="w-3 h-3 mr-1" />Material
                      </Button>
                    </div>
                    {(item.materials || []).map((mat) => (
                      <div key={mat.matId} className="mb-2 p-2 bg-white rounded border border-blue-100" data-testid={`material-row-${mat.matId}`}>
                        {/* Input pentru căutare/denumire material */}
                        <div className="relative mb-2">
                          <div className="flex items-center gap-1 mb-1">
                            <Search className="w-3 h-3 text-slate-400" />
                            <Label className="text-xs text-slate-500">Denumire material</Label>
                            {mat.fromInventory && (
                              <Badge variant="outline" className="text-[10px] h-4 bg-green-50 text-green-600 border-green-200 ml-auto">
                                {mat.autoAdded ? 'Adăugat auto' : 'Din inventar'}
                              </Badge>
                            )}
                          </div>
                          <Input
                            placeholder="Caută sau scrie denumirea..."
                            value={mat.name}
                            onChange={(e) => {
                              updateItemMaterial(item.id, mat.matId, 'name', e.target.value);
                              // Reset fromInventory când utilizatorul modifică manual
                              if (mat.fromInventory) {
                                updateItemMaterial(item.id, mat.matId, 'fromInventory', false);
                                updateItemMaterial(item.id, mat.matId, 'inventoryItemId', null);
                              }
                            }}
                            className="h-8 text-sm bg-white"
                            data-testid={`material-name-input-${mat.matId}`}
                          />
                          
                          {/* Lista scrollabilă cu sugestii din inventar */}
                          {!mat.fromInventory && (
                            <div className="mt-1 border rounded-lg bg-slate-50 max-h-32 overflow-y-auto">
                              <p className="text-[10px] text-slate-400 px-2 py-1 border-b bg-slate-100 sticky top-0">
                                {mat.name ? 'Sugestii din inventar:' : 'Toate materialele din inventar:'}
                              </p>
                              {inventoryItems
                                .filter(inv => !mat.name || inv.name?.toLowerCase().includes(mat.name.toLowerCase()))
                                .slice(0, 20)
                                .map((inv) => (
                                  <button 
                                    key={inv.id} 
                                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-100 border-b border-slate-100 flex justify-between items-center"
                                    onClick={() => selectInventoryForMaterial(item.id, mat.matId, inv)}
                                    data-testid={`inventory-suggestion-${inv.id}`}
                                  >
                                    <span className="font-medium truncate flex-1">{inv.name}</span>
                                    <span className="text-slate-400 ml-2 whitespace-nowrap">{inv.um} • {inv.material_price} RON</span>
                                  </button>
                                ))}
                              {inventoryItems.filter(inv => !mat.name || inv.name?.toLowerCase().includes(mat.name.toLowerCase())).length === 0 && (
                                <p className="text-xs text-slate-400 px-2 py-2 text-center">
                                  {mat.name ? 'Niciun material găsit. Continuă să scrii pentru a crea unul nou.' : 'Inventarul este gol.'}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Grid cu cantitate, um, preț */}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] text-slate-500">Cantitate</Label>
                            <Input 
                              type="number" 
                              placeholder="0" 
                              value={mat.quantity === 0 || mat.quantity === '' || mat.quantity == null ? '' : mat.quantity} 
                              onChange={(e) => updateItemMaterial(item.id, mat.matId, 'quantity', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} 
                              className="h-7 text-xs bg-yellow-50"
                              data-testid={`material-qty-${mat.matId}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">U.M.</Label>
                            <Input 
                              placeholder="buc" 
                              value={mat.um || ''} 
                              onChange={(e) => updateItemMaterial(item.id, mat.matId, 'um', e.target.value)} 
                              className="h-7 text-xs bg-white"
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Preț/U.M.</Label>
                            <Input 
                              type="number" 
                              placeholder="0" 
                              value={mat.price === 0 || mat.price === '' || mat.price == null ? '' : mat.price} 
                              onChange={(e) => updateItemMaterial(item.id, mat.matId, 'price', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} 
                              className="h-7 text-xs bg-yellow-50"
                            />
                          </div>
                        </div>
                        
                        {/* Acțiuni și total */}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                          <div className="flex gap-1">
                            {mat.name && !mat.fromInventory && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => addNewMaterialToInventory(item.id, mat.matId, mat.name, mat.um, mat.price)} 
                                className="h-6 text-[10px] text-orange-600 border-orange-200 hover:bg-orange-50"
                                title="Adaugă materialul nou în inventar"
                                data-testid={`add-to-inventory-${mat.matId}`}
                              >
                                <PlusCircle className="w-3 h-3 mr-1" />
                                În inventar
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => removeItemMaterial(item.id, mat.matId)} 
                              className="h-6 text-red-400 hover:text-red-600 p-1"
                              data-testid={`remove-material-${mat.matId}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-xs font-semibold text-blue-700">
                            Total: {formatNumber((mat.quantity || 0) * (mat.price || 0))} {currencyCode}
                          </p>
                        </div>
                      </div>
                    ))}
                    {(item.materials || []).length > 0 && (
                      <div className="text-right text-xs font-semibold text-blue-700 mt-1 pt-1 border-t border-blue-200">
                        Total materiale lucrare: {formatNumber((item.materials || []).reduce((s, m) => s + (m.quantity * m.price), 0))} {currencyCode}
                      </div>
                    )}
                    {(item.materials || []).length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-2">
                        Apasă "+ Material" pentru a adăuga materiale consumate
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Buton adăugare în partea de jos pe mobil */}
              <Button onClick={addWorkItem} variant="outline" className="w-full border-dashed border-2">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Lucrare Nouă
              </Button>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto scrollbar-hide" style={{WebkitOverflowScrolling: 'touch'}}>
              <div className="min-w-[700px]">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border p-2 w-10">Nr.</th>
                    <th className="border p-2">Capitol de lucrări</th>
                    <th className="border p-2 w-16">U.M.</th>
                    <th className="border p-2 w-20 bg-yellow-100">Cantitate</th>
                    <th className="border p-2 w-24 bg-yellow-100">Preț Material</th>
                    <th className="border p-2 w-24 bg-yellow-100">Preț Manoperă</th>
                    <th className="border p-2 w-24">Val. Material</th>
                    <th className="border p-2 w-24">Val. Manoperă</th>
                    <th className="border p-2 w-24">Total</th>
                    <th className="border p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {workItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                    <tr>
                      <td className="border p-1 text-center w-12">
                        <OrderBadge
                          index={index}
                          total={workItems.length}
                          onReorder={(from, to) => setWorkItems((arr) => moveItem(arr, from, to))}
                          variant="table"
                          testIdPrefix="deviz-order-table"
                        />
                      </td>
                      <td className="border p-2">
                        <div 
                          className="cursor-pointer hover:bg-yellow-100 p-1 rounded min-h-[2rem] flex items-center"
                          onClick={() => {
                            setEditingItem(item);
                            setEditingName(item.name);
                          }}
                        >
                          <span className="line-clamp-2 text-sm">
                            {item.name || <span className="text-slate-400 italic">Click pentru a edita...</span>}
                          </span>
                        </div>
                      </td>
                      <td className="border p-2">
                        <Select 
                          value={item.um} 
                          onValueChange={(v) => updateWorkItem(item.id, 'um', v)}
                        >
                          <SelectTrigger className="border-0 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mp">mp</SelectItem>
                            <SelectItem value="ml">ml</SelectItem>
                            <SelectItem value="mc">mc</SelectItem>
                            <SelectItem value="buc">buc</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="ora">ora</SelectItem>
                            <SelectItem value="to">to</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border p-2 bg-yellow-50">
                        <Input
                          type="number"
                          value={item.quantity === 0 || item.quantity === '' || item.quantity == null ? '' : item.quantity}
                          onChange={(e) => updateWorkItem(item.id, 'quantity', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                          className="border-0 p-0 h-auto text-right bg-transparent"
                        />
                      </td>
                      <td className="border p-2 bg-yellow-50">
                        <Input
                          type="number"
                          value={item.materialPrice === 0 || item.materialPrice === '' || item.materialPrice == null ? '' : item.materialPrice}
                          onChange={(e) => updateWorkItem(item.id, 'materialPrice', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                          className="border-0 p-0 h-auto text-right bg-transparent"
                        />
                      </td>
                      <td className="border p-2 bg-yellow-50">
                        <Input
                          type="number"
                          value={item.manoperaPrice === 0 || item.manoperaPrice === '' || item.manoperaPrice == null ? '' : item.manoperaPrice}
                          onChange={(e) => updateWorkItem(item.id, 'manoperaPrice', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                          className="border-0 p-0 h-auto text-right bg-transparent"
                        />
                      </td>
                      <td className="border p-2 text-right font-medium text-blue-700">
                        {formatNumber(item.quantity * item.materialPrice)}
                      </td>
                      <td className="border p-2 text-right font-medium text-orange-700">
                        {formatNumber(item.quantity * item.manoperaPrice)}
                      </td>
                      <td className="border p-2 text-right font-bold">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity * (item.materialPrice + item.manoperaPrice)}
                          onChange={(e) => {
                            const newTotal = parseFloat(e.target.value) || 0;
                            const qty = parseFloat(item.quantity) || 0;
                            if (qty <= 0) return;
                            const currentUnit = (parseFloat(item.materialPrice) || 0) + (parseFloat(item.manoperaPrice) || 0);
                            const newUnit = newTotal / qty;
                            // Distribuie noul preț unitar proporțional între material și manoperă
                            let newMat, newMan;
                            if (currentUnit > 0) {
                              const ratio = (parseFloat(item.materialPrice) || 0) / currentUnit;
                              newMat = Math.round(newUnit * ratio * 100) / 100;
                              newMan = Math.round((newUnit - newMat) * 100) / 100;
                            } else {
                              newMat = Math.round((newUnit / 2) * 100) / 100;
                              newMan = Math.round((newUnit - newMat) * 100) / 100;
                            }
                            updateWorkItem(item.id, "materialPrice", newMat);
                            updateWorkItem(item.id, "manoperaPrice", newMan);
                          }}
                          title="Introdu valoarea totală dorită → prețul unitar se calculează automat (valoare/cantitate)"
                          className="border-0 p-0 h-auto text-right bg-transparent font-bold hover:bg-yellow-50 focus:bg-white focus:border focus:border-yellow-400 focus:px-1"
                          data-testid={`workitem-value-${item.id}`}
                        />
                      </td>
                      <td className="border p-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeWorkItem(item.id)}
                          className="h-6 w-6 p-0 text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                    {/* Materials row */}
                    <tr>
                      <td className="border-x border-b p-0" colSpan={10}>
                        <div className="bg-blue-50 px-3 py-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-blue-700 flex items-center gap-1"><Package className="w-3 h-3" /> Materiale consumate</span>
                            <Button size="sm" variant="ghost" onClick={() => addMaterialToItem(item.id)} className="h-5 text-blue-600 text-xs p-1" data-testid={`desktop-add-material-${item.id}`}><Plus className="w-3 h-3 mr-1" />Material</Button>
                          </div>
                          {(item.materials || []).map((mat) => (
                            <div key={mat.matId} className="flex gap-2 items-start py-2 border-b border-blue-100 last:border-0" data-testid={`desktop-material-${mat.matId}`}>
                              <div className="flex-1 min-w-[200px]">
                                <div className="relative">
                                  <Input 
                                    placeholder="Caută sau scrie denumirea materialului..." 
                                    value={mat.name} 
                                    onChange={(e) => {
                                      updateItemMaterial(item.id, mat.matId, 'name', e.target.value);
                                      if (mat.fromInventory) {
                                        updateItemMaterial(item.id, mat.matId, 'fromInventory', false);
                                        updateItemMaterial(item.id, mat.matId, 'inventoryItemId', null);
                                      }
                                    }} 
                                    className="h-8 text-xs bg-white"
                                    data-testid={`desktop-material-name-${mat.matId}`}
                                  />
                                  {/* Lista scrollabilă cu sugestii */}
                                  {!mat.fromInventory && (
                                    <div className="mt-1 border rounded bg-white shadow-sm max-h-28 overflow-y-auto">
                                      <p className="text-[10px] text-slate-400 px-2 py-0.5 bg-slate-50 sticky top-0 border-b">
                                        {mat.name ? `Sugestii pentru "${mat.name}":` : 'Selectează din inventar:'}
                                      </p>
                                      {inventoryItems
                                        .filter(inv => !mat.name || inv.name?.toLowerCase().includes(mat.name.toLowerCase()))
                                        .slice(0, 15)
                                        .map((inv) => (
                                          <button 
                                            key={inv.id} 
                                            className="w-full text-left px-2 py-1 text-xs hover:bg-blue-50 border-b border-slate-50 flex justify-between"
                                            onClick={() => selectInventoryForMaterial(item.id, mat.matId, inv)}
                                          >
                                            <span className="truncate flex-1">{inv.name}</span>
                                            <span className="text-slate-400 ml-2 whitespace-nowrap">{inv.um} • {formatNumber(inv.material_price)} RON</span>
                                          </button>
                                        ))}
                                      {inventoryItems.filter(inv => !mat.name || inv.name?.toLowerCase().includes(mat.name.toLowerCase())).length === 0 && (
                                        <p className="text-[10px] text-slate-400 px-2 py-1 text-center">
                                          {mat.name ? 'Niciun rezultat. Material nou.' : 'Inventar gol.'}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  {mat.fromInventory && (
                                    <Badge variant="outline" className="absolute right-1 top-1.5 text-[9px] h-4 bg-green-50 text-green-600 border-green-200">
                                      {mat.autoAdded ? 'Adăugat auto' : 'Din inventar'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Input type="number" placeholder="Cant." value={mat.quantity === 0 || mat.quantity === '' || mat.quantity == null ? '' : mat.quantity} onChange={(e) => updateItemMaterial(item.id, mat.matId, 'quantity', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} className="w-20 h-8 text-xs bg-yellow-50" />
                                <Input placeholder="U.M." value={mat.um || ''} onChange={(e) => updateItemMaterial(item.id, mat.matId, 'um', e.target.value)} className="w-14 h-8 text-xs bg-white" />
                                <Input type="number" placeholder="Preț" value={mat.price === 0 || mat.price === '' || mat.price == null ? '' : mat.price} onChange={(e) => updateItemMaterial(item.id, mat.matId, 'price', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))} className="w-20 h-8 text-xs bg-yellow-50" />
                                <span className="text-xs font-semibold text-blue-700 w-20 text-right">{formatNumber((mat.quantity || 0) * (mat.price || 0))} RON</span>
                                {mat.name && !mat.fromInventory && (
                                  <Button size="sm" variant="outline" onClick={() => addNewMaterialToInventory(item.id, mat.matId, mat.name, mat.um, mat.price)} className="h-7 text-[10px] text-orange-600 border-orange-200 px-2" title="Adaugă în inventar">
                                    <PlusCircle className="w-3 h-3 mr-1" />În inv.
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => removeItemMaterial(item.id, mat.matId)} className="h-7 w-7 text-red-400 hover:text-red-600 p-0"><Trash2 className="w-3 h-3" /></Button>
                              </div>
                            </div>
                          ))}
                          {(item.materials || []).length > 0 && (
                            <div className="text-right text-xs font-semibold text-blue-700 pt-2 border-t border-blue-200 mt-1">
                              Total materiale lucrare: {formatNumber((item.materials || []).reduce((s, m) => s + ((m.quantity || 0) * (m.price || 0)), 0))} RON
                            </div>
                          )}
                          {(item.materials || []).length === 0 && (
                            <p className="text-[10px] text-slate-400 text-center py-1">
                              Click "+ Material" pentru a adăuga materiale
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>

          {/* Percentages Section */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <h3 className="font-semibold mb-3">Procente Calcul</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Contribuție muncă (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={percentages.contributieMunca === 0 || percentages.contributieMunca === '' ? '' : percentages.contributieMunca}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPercentages({...percentages, contributieMunca: v === '' ? '' : parseFloat(v)});
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                      setPercentages({...percentages, contributieMunca: 0});
                    }
                  }}
                  className="bg-yellow-100 border-yellow-300"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Cheltuieli indirecte (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={percentages.cheltuieliIndirecte === 0 || percentages.cheltuieliIndirecte === '' ? '' : percentages.cheltuieliIndirecte}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPercentages({...percentages, cheltuieliIndirecte: v === '' ? '' : parseFloat(v)});
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                      setPercentages({...percentages, cheltuieliIndirecte: 0});
                    }
                  }}
                  className="bg-yellow-100 border-yellow-300"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Profit (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={percentages.profit === 0 || percentages.profit === '' ? '' : percentages.profit}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPercentages({...percentages, profit: v === '' ? '' : parseFloat(v)});
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                      setPercentages({...percentages, profit: 0});
                    }
                  }}
                  className="bg-yellow-100 border-yellow-300"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">TVA (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={percentages.tva === 0 || percentages.tva === '' ? '' : percentages.tva}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPercentages({...percentages, tva: v === '' ? '' : parseFloat(v)});
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                      setPercentages({...percentages, tva: 0});
                    }
                  }}
                  className="bg-yellow-100 border-yellow-300"
                />
              </div>
            </div>
          </div>

          {/* SECȚIUNEA FINANCIARĂ - Mobile Card View */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold text-base sm:text-lg">SECȚIUNEA FINANCIARĂ - Recapitulație</h3>
              {/* Toggle RON / EUR + curs editabil */}
              <div className="flex items-center gap-2 bg-slate-50 border rounded-lg px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => setCurrency("RON")}
                  className={`px-2.5 py-1 rounded text-xs font-semibold transition ${currency === "RON" ? "bg-teal-600 text-white shadow" : "text-slate-600 hover:bg-slate-200"}`}
                  data-testid="currency-ron"
                >RON</button>
                <button
                  type="button"
                  onClick={() => setCurrency("EUR")}
                  className={`px-2.5 py-1 rounded text-xs font-semibold transition ${currency === "EUR" ? "bg-teal-600 text-white shadow" : "text-slate-600 hover:bg-slate-200"}`}
                  data-testid="currency-eur"
                >EUR</button>
                {isEUR && (
                  <div className="flex items-center gap-1 pl-2 border-l">
                    <span className="text-xs text-slate-500">Curs:</span>
                    <Input
                      type="number"
                      step="0.0001"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0)}
                      className="h-7 w-20 text-xs"
                      data-testid="currency-rate-input"
                    />
                    {fxLoadedDate && (
                      <span className="text-[10px] text-slate-400 hidden sm:inline" title={`BNR ${fxLoadedDate}`}>BNR</span>
                    )}
                  </div>
                )}
                {/* Buton discret de re-etichetare a monedei fără conversie. Util când admin
                    a introdus prețurile deja în EUR dar toggle-ul era pe RON (sau invers).
                    Click → setează moneda dorită cu curs 1.0, deci valorile NU se modifică. */}
                <CurrencyRelabelMenu setCurrency={setCurrency} setExchangeRate={setExchangeRate} />
              </div>
            </div>
            
            {/* Mobile View */}
            <div className="block sm:hidden space-y-2">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">TOTAL 1 (Cheltuieli directe)</p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Material</p>
                    <p className="font-semibold">{formatNumber(totals.totalMaterial)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Manoperă</p>
                    <p className="font-semibold">{formatNumber(totals.totalManopera)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="font-bold">{formatNumber(totals.totalDirect)}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-100 p-3 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">TOTAL GENERAL (fără TVA)</p>
                <p className="text-xl font-bold text-green-700 text-center">{formatNumber(totals.totalFaraTVA)} {currencyCode}</p>
              </div>
              
              <div className="bg-slate-100 p-3 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">TVA ({percentages.tva}%)</p>
                <p className="text-lg font-semibold text-center">{formatNumber(totals.tvaValue)} {currencyCode}</p>
              </div>
              
              <div className="bg-gradient-to-r from-green-600 to-teal-600 p-4 rounded-lg text-white relative">
                <p className="text-xs opacity-80 mb-1">TOTAL GENERAL (inclusiv TVA)</p>
                <p className="text-2xl font-bold text-center">{formatNumber(totals.totalCuTVA)} {currencyCode}</p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowInverseCalc(true)}
                  className="absolute top-1 right-1 text-white hover:bg-white/20 h-7 px-2"
                  title="Ajustare preț țintă"
                >
                  <Target className="w-4 h-4 mr-1" />
                  <span className="text-xs">Setează preț</span>
                </Button>
              </div>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border p-2 text-left">Denumire</th>
                    <th className="border p-2 text-right w-28">Material</th>
                    <th className="border p-2 text-right w-28">Manoperă</th>
                    <th className="border p-2 text-right w-32">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-blue-50">
                    <td className="border p-2 font-medium">TOTAL 1 (Cheltuieli directe)</td>
                    <td className="border p-2 text-right">{formatNumber(totals.totalMaterial)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.totalManopera)}</td>
                    <td className="border p-2 text-right font-medium">{formatNumber(totals.totalDirect)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border p-2" colSpan={4}><strong>Alte cheltuieli directe</strong></td>
                  </tr>
                  <tr>
                    <td className="border p-2 pl-6">Contribuția asiguratorie ({percentages.contributieMunca}%)</td>
                    <td className="border p-2 text-right text-slate-400">-</td>
                    <td className="border p-2 text-right">{formatNumber(totals.contributieMunca)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.contributieMunca)}</td>
                  </tr>
                  <tr className="bg-blue-50">
                    <td className="border p-2 font-medium">T2 = T1 + Alte cheltuieli directe</td>
                    <td className="border p-2 text-right">{formatNumber(totals.t2Material)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.t2Manopera)}</td>
                    <td className="border p-2 text-right font-medium">{formatNumber(totals.t2Material + totals.t2Manopera)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border p-2" colSpan={4}><strong>Cheltuieli indirecte</strong></td>
                  </tr>
                  <tr>
                    <td className="border p-2 pl-6">Cheltuieli indirecte ({percentages.cheltuieliIndirecte}%)</td>
                    <td className="border p-2 text-right">{formatNumber(totals.cheltuieliIndirecteMaterial)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.cheltuieliIndirecteManopera)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.cheltuieliIndirecteMaterial + totals.cheltuieliIndirecteManopera)}</td>
                  </tr>
                  <tr className="bg-blue-50">
                    <td className="border p-2 font-medium">T3 = T2 + Cheltuieli indirecte</td>
                    <td className="border p-2 text-right">{formatNumber(totals.t3Material)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.t3Manopera)}</td>
                    <td className="border p-2 text-right font-medium">{formatNumber(totals.t3Material + totals.t3Manopera)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border p-2" colSpan={4}><strong>Beneficiu</strong></td>
                  </tr>
                  <tr>
                    <td className="border p-2 pl-6">Profit ({percentages.profit}%)</td>
                    <td className="border p-2 text-right">{formatNumber(totals.profitMaterial)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.profitManopera)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.profitMaterial + totals.profitManopera)}</td>
                  </tr>
                  <tr className="bg-blue-50">
                    <td className="border p-2 font-medium">T4 = T3 + Beneficiu</td>
                    <td className="border p-2 text-right">{formatNumber(totals.t4Material)}</td>
                    <td className="border p-2 text-right">{formatNumber(totals.t4Manopera)}</td>
                    <td className="border p-2 text-right font-medium">{formatNumber(totals.t4Material + totals.t4Manopera)}</td>
                  </tr>
                  <tr className="bg-green-100">
                    <td className="border p-2 font-bold" colSpan={3}>TOTAL GENERAL (fără TVA)</td>
                    <td className="border p-2 text-right font-bold text-lg">{formatNumber(totals.totalFaraTVA)} {currencyCode}</td>
                  </tr>
                  <tr>
                    <td className="border p-2" colSpan={3}>TVA ({percentages.tva}%)</td>
                    <td className="border p-2 text-right">{formatNumber(totals.tvaValue)} {currencyCode}</td>
                  </tr>
                  <tr className="bg-green-600 text-white">
                    <td className="border border-green-700 p-3 font-bold text-lg" colSpan={3}>TOTAL GENERAL (inclusiv TVA)</td>
                    <td className="border border-green-700 p-3 text-right font-bold text-xl">{formatNumber(totals.totalCuTVA)} {currencyCode}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Secțiunea Gestiune Materiale */}
          {assignedMaterials.length > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Materiale din Gestiune ({assignedMaterials.length})
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { fetchInventory(); setShowMaterialsDialog(true); }}
                  className="text-amber-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adaugă
                </Button>
              </div>
              <div className="space-y-2">
                {assignedMaterials.map((mat) => (
                  <div key={mat.item_id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                    <div className="flex-1">
                      <p className="font-medium">{mat.item_name}</p>
                      <p className="text-xs text-slate-500">Preț: {formatNumber(mat.material_price)} RON/{mat.um} | Stoc: {mat.stock_available} {mat.um}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={mat.quantity}
                        onChange={(e) => updateMaterialQuantity(mat.item_id, e.target.value)}
                        className="w-20 text-center"
                        min="0"
                        step="0.1"
                      />
                      <span className="text-sm text-slate-500">{mat.um}</span>
                      <span className="text-sm font-medium text-amber-700 w-24 text-right">
                        {formatNumber(mat.quantity * mat.material_price)} RON
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => removeMaterialFromDeviz(mat.item_id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-amber-200 font-semibold text-amber-800">
                  <span>Total Materiale Gestiune:</span>
                  <span>{formatNumber(assignedMaterials.reduce((sum, m) => sum + m.quantity * m.material_price, 0))} RON</span>
                </div>
              </div>
            </div>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mt-8 pt-4 border-t">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="font-medium mb-2 text-slate-700">BENEFICIAR</p>
              <p className="font-semibold text-base sm:text-lg mb-4 sm:mb-8">{devizInfo.beneficiar || '____________________'}</p>
              <p className="border-t border-slate-300 pt-2 text-xs sm:text-sm text-slate-500">Semnătură și ștampilă</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="font-medium mb-2 text-slate-700">EXECUTANT</p>
              <p className="font-semibold text-base sm:text-lg mb-4 sm:mb-8">{devizInfo.executant || '____________________'}</p>
              <p className="border-t border-slate-300 pt-2 text-xs sm:text-sm text-slate-500">Semnătură și ștampilă</p>
            </div>
          </div>
        </ScrollArea>
      </CardContent>

      {/* Dialog: Trimitere ofertă pe email beneficiarului */}
      <SendOfferDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        emailRecipient={emailRecipient}
        setEmailRecipient={setEmailRecipient}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        emailIncludeMaterials={emailIncludeMaterials}
        setEmailIncludeMaterials={setEmailIncludeMaterials}
        sendingEmail={sendingEmail}
        beneficiarName={devizInfo.beneficiar}
        onSend={handleSendOffer}
        onCancel={() => setShowEmailDialog(false)}
      />

      <ContractSelectorDialog
        open={showContractSelector}
        onOpenChange={setShowContractSelector}
        loadingContracts={loadingContracts}
        contractsList={contractsList}
        onSelect={handleSelectContract}
      />

      <InverseCalcDialog
        open={showInverseCalc}
        onOpenChange={setShowInverseCalc}
        currentPriceFormatted={formatNumber(totals.totalCuTVA)}
        targetPrice={targetPrice}
        setTargetPrice={setTargetPrice}
        onCalculate={calculateInverseIndirecte}
        mode={inverseMode}
        setMode={setInverseMode}
      />

      {/* Preview Dialog — shows the same PDF format as "Descarcă PDF" */}
      <Dialog
        open={showPreview}
        onOpenChange={(o) => {
          setShowPreview(o);
          if (!o && pdfPreviewUrl) {
            try { URL.revokeObjectURL(pdfPreviewUrl); } catch { /* ignore */ }
            setPdfPreviewUrl(null);
          }
        }}
      >
        <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden p-3 sm:p-4 flex flex-col" data-testid="deviz-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="text-sm sm:text-base truncate">Previzualizare Deviz</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleGeneratePDF(false, 'save')}
                  className="text-green-700 border-green-300 hover:bg-green-50 hidden sm:inline-flex"
                  data-testid="preview-download-btn"
                >
                  <FileText className="w-4 h-4 mr-1" /> Descarcă PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePrint}
                  className="text-blue-700 border-blue-300 hover:bg-blue-50"
                  data-testid="preview-print-btn"
                >
                  <Printer className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Printează</span>
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 border rounded-lg bg-slate-100 overflow-hidden overflow-y-auto" style={{ minHeight: '70vh' }}>
            {isMobileView ? (
              <MobileDevizPreview
                devizInfo={devizInfo}
                workItems={workItems}
                totals={totals}
                currencyCode={currencyCode}
                exchangeRate={exchangeRate}
                isEUR={isEUR}
                onDownloadPDF={() => handleGeneratePDF(false, 'save')}
              />
            ) : pdfPreviewUrl ? (
              <iframe
                src={pdfPreviewUrl}
                title="Previzualizare Deviz PDF"
                className="w-full h-full"
                style={{ minHeight: '70vh', border: 'none' }}
                data-testid="deviz-pdf-preview-iframe"
              />
            ) : (
              <MobileDevizPreview
                devizInfo={devizInfo}
                workItems={workItems}
                totals={totals}
                currencyCode={currencyCode}
                exchangeRate={exchangeRate}
                isEUR={isEUR}
                onDownloadPDF={() => handleGeneratePDF(false, 'save')}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <EditItemDialog
        open={editingItem}
        editingItem={editingItem}
        editingName={editingName}
        setEditingName={setEditingName}
        onCancel={() => setEditingItem(null)}
        onSave={(id, name) => { updateWorkItem(id, "name", name); setEditingItem(null); }}
      />

      <MaterialsPickerDialog
        open={showMaterialsDialog}
        onOpenChange={setShowMaterialsDialog}
        loadingInventory={loadingInventory}
        filteredInventory={filteredInventory}
        assignedMaterials={assignedMaterials}
        materialsSearchTerm={materialsSearchTerm}
        setMaterialsSearchTerm={setMaterialsSearchTerm}
        onAddMaterial={addMaterialToDeviz}
        formatNumber={formatNumber}
      />
    </Card>
  );
}
